/**
 * Adapt Zuntly Marketplace (B2B) orders into the same shape Online/Offline sales use,
 * so Orders cards + OrderOverview can render one UI.
 */

export const MARKETPLACE_STATUS_FLOW = [
    'Received',
    'Confirmed',
    'Packed',
    'Shipped',
    'Delivered',
    'Cancelled',
];

export const MARKETPLACE_ORDER_TABS = [
    { label: 'All', apiValue: 'All' },
    { label: 'Received', apiValue: 'Received' },
    { label: 'Confirmed', apiValue: 'Confirmed' },
    { label: 'Packed', apiValue: 'Packed' },
    { label: 'Shipped', apiValue: 'Shipped' },
    { label: 'Delivered', apiValue: 'Delivered' },
    { label: 'Cancelled', apiValue: 'Cancelled' },
];

/** Normalize API image field (string | {preview} | nested) → URL string */
export function resolveMarketplaceImageUrl(image) {
    if (!image) return null;
    if (typeof image === 'string') {
        const t = image.trim();
        return t || null;
    }
    if (Array.isArray(image)) {
        for (const entry of image) {
            const url = resolveMarketplaceImageUrl(entry);
            if (url) return url;
        }
        return null;
    }
    if (typeof image === 'object') {
        if (typeof image.preview === 'string' && image.preview.trim()) return image.preview.trim();
        if (image.preview && typeof image.preview === 'object') {
            return resolveMarketplaceImageUrl(image.preview);
        }
        if (typeof image.url === 'string' && image.url.trim()) return image.url.trim();
        if (typeof image.uri === 'string' && image.uri.trim()) return image.uri.trim();
        if (typeof image.original === 'string' && image.original.trim()) return image.original.trim();
        if (typeof image.imageUrl === 'string' && image.imageUrl.trim()) return image.imageUrl.trim();
    }
    return null;
}

function mapMarketplaceItem(item = {}) {
    const discountPrice = Number(
        item.discountPrice
        ?? item.pricing?.discountPrice
        ?? (item.lineTotal && item.quantity ? Number(item.lineTotal) / Number(item.quantity) : 0)
        ?? 0
    ) || 0;
    const originalPrice = Number(item.originalPrice ?? item.pricing?.originalPrice ?? discountPrice) || 0;
    const optionLabel = String(
        item.optionName
        || item.optionLabel
        || item.metric
        || (item.weightValue ? `${item.weightValue}${item.metric ? ` ${item.metric}` : ''}` : '')
        || ''
    ).trim();
    const preview = resolveMarketplaceImageUrl(item.image)
        || resolveMarketplaceImageUrl(item.images)
        || resolveMarketplaceImageUrl(item.selectedWeight?.images);

    // Match sales-order shape: selectedWeight.weight as { weightLabel } so UI never shows (N/A)
    const weightObj = {
        weightLabel: optionLabel || 'Pack',
        weightValue: item.weightValue != null ? Number(item.weightValue) : null,
        metricLabel: item.metric || '',
    };

    return {
        _id: item._id || item.optionId || item.productId,
        productId: item.productId,
        productName: item.productName || 'Item',
        description: optionLabel || 'Marketplace pack',
        quantity: Number(item.quantity) || 0,
        images: preview ? { preview } : undefined,
        image: preview || undefined,
        selectedWeight: {
            weight: weightObj,
            weightLabel: weightObj.weightLabel,
            discountPrice,
            originalPrice,
            images: preview ? [{ preview }] : [],
        },
        pricing: {
            discountPrice,
            originalPrice,
        },
        itemStatus: item.itemStatus || undefined,
        isCancelled: false,
        isReturned: false,
    };
}

function mapAddress(deliveryAddress = {}, seller = {}) {
    const addr = deliveryAddress || {};
    const line =
        addr.address
        || addr.addressLine
        || addr.addressLine1
        || [addr.house, addr.area, addr.landmark, addr.city, addr.pincode, addr.state].filter(Boolean).join(', ');

    return {
        name: addr.name || seller.businessName || seller.name || 'Seller',
        phone: addr.phone || addr.mobile || seller.phone || '',
        address: line || '',
        house: addr.house || '',
        area: addr.area || '',
        landmark: addr.landmark || '',
        city: addr.city || '',
        state: addr.state || '',
        pincode: addr.pincode || addr.zip || '',
        addressLine1: addr.addressLine1 || addr.addressLine || '',
        directions: addr.directions || '',
    };
}

/**
 * Full order document for OrderOverview (mirrors sales order fields the page reads).
 * Intentionally omits incomplete billSummary so OrderOverview recalculates from productsInfo.
 */
export function adaptMarketplaceOrderForOverview(order) {
    if (!order) return null;
    const items = (order.items || []).map(mapMarketplaceItem);
    const seller = order.seller || {};
    const addressInfo = mapAddress(order.deliveryAddress, seller);
    const financial = order.financial || {};

    const unitCount = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
    const originalTotal = items.reduce((s, i) => {
        const price = Number(i.selectedWeight?.originalPrice || i.pricing?.originalPrice || 0);
        return s + price * (Number(i.quantity) || 0);
    }, 0);
    const discountedSubtotal = items.reduce((s, i) => {
        const price = Number(i.selectedWeight?.discountPrice || i.pricing?.discountPrice || 0);
        return s + price * (Number(i.quantity) || 0);
    }, 0);
    const couponDiscount = Number(financial.discount || financial.coupon?.discount || financial.couponDiscount || 0);
    const finalAmount = Number(
        financial.finalAmount
        ?? Math.max(0, (Number(financial.subtotal) || discountedSubtotal) - couponDiscount)
    );
    const subtotal = Number(financial.subtotal || discountedSubtotal);

    // Full billSummary so Bill Details never shows zeros when Final Total is correct
    const billSummary = {
        itemUnitCount: unitCount,
        itemLineCount: items.length,
        originalTotal: originalTotal || subtotal,
        productDiscount: Math.max(0, (originalTotal || subtotal) - discountedSubtotal),
        discountedSubtotal: discountedSubtotal || subtotal,
        couponDiscount,
        freeCashDiscount: 0,
        deliveryCharges: 0,
        finalAmount,
    };

    const { billSummary: _dropIncomplete, ...financialRest } = financial;

    return {
        ...order,
        isMarketplaceOrder: true,
        orderType: 'marketplace',
        saleId: order.marketplaceOrderId || order.saleId,
        orderId: order.marketplaceOrderId || order.orderId,
        orderDate: order.createdAt || order.orderDate,
        orderPlacedOn: order.createdAt || order.orderPlacedOn,
        status: order.status || 'Received',
        statusHistory: (order.statusHistory || []).map((h) => ({
            ...h,
            statusTo: h.statusTo || h.status,
            changedOn: h.changedOn || h.at || h.timestamp,
            timestamp: h.timestamp || h.at || h.changedOn,
        })),
        productsInfo: items,
        customer: {
            customerName: seller.businessName || seller.name || 'Marketplace Seller',
            customerPhone: seller.phone || '',
            customerEmail: seller.email || '',
            gstNumber: seller.gstNumber || '',
        },
        addressInfo,
        financial: {
            ...financialRest,
            subtotal,
            finalAmount,
            couponDiscount,
            deliveryCharges: 0,
            freeCashDiscount: 0,
            totalOriginalPrice: billSummary.originalTotal,
            totalMrp: billSummary.originalTotal,
            billSummary,
        },
        payment: {
            paymentMode: order.paymentMode || financial.paymentMode || 'COD',
        },
        delivery: {
            deliveryOption: { type: 'Marketplace (B2B)', source: 'marketplace' },
        },
        isOffline: false,
        isManualDelivery: false,
        deliveryDriver: null,
        branch: order.branch || {
            branchName: order.storeName || 'Store',
            branchId: order.storeAdminId || order.masterId,
        },
        notes: order.notes || '',
        gstInvoice: seller.gstNumber
            ? { requested: true, gstin: seller.gstNumber, businessName: seller.businessName || seller.name }
            : order.gstInvoice,
    };
}

/**
 * Compact card fields for Orders.js grid (same keys as online/offline mapped cards).
 */
export function adaptMarketplaceOrderForCard(order, formatDateToLong, getOrderItemImage) {
    const adapted = adaptMarketplaceOrderForOverview(order);
    const productsInfo = adapted.productsInfo || [];
    const first = productsInfo[0] || {};
    const activeUnitCount = productsInfo.reduce((s, item) => s + (item.quantity ?? 0), 0);
    const totalAmount = Number(adapted.financial?.finalAmount || 0);
    const couponDiscount = Number(adapted.financial?.couponDiscount || 0);
    const resolvedImage =
        resolveMarketplaceImageUrl(first.images)
        || resolveMarketplaceImageUrl(first.image)
        || resolveMarketplaceImageUrl(first.selectedWeight?.images)
        || (typeof getOrderItemImage === 'function' ? getOrderItemImage(first) : null);

    return {
        _id: adapted._id,
        saleId: adapted.saleId,
        name: first.productName || 'N/A',
        date: formatDateToLong(adapted.orderDate, true),
        description: first.description || 'Marketplace order',
        amount: adapted.financial?.subtotal || 0,
        quantity: first.quantity ?? 0,
        itemsCount: activeUnitCount || productsInfo.length,
        lineItemsCount: productsInfo.length,
        status: adapted.status,
        firstItemDiscountPrice: first.selectedWeight?.discountPrice || first.pricing?.discountPrice || 0,
        image: resolvedImage || null,
        productsInfo,
        totalAmount,
        deliveryCharges: 0,
        couponDiscount,
        freeCashDiscount: 0,
        hasPartialReturn: false,
        customerName: adapted.customer?.customerName || 'N/A',
        cancellationInfo: null,
        customerPhone: adapted.customer?.customerPhone || 'N/A',
        isOffline: false,
        isMarketplaceOrder: true,
        isManualDelivery: false,
        deliveryDriver: null,
        branchId: adapted.branch?.branchId || null,
        branchName: adapted.branch?.branchName || 'N/A',
    };
}
